import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHeader, Loader, Badge, Empty } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { fmtPKR, fmtDate, statusColor } from "@/lib/format";

const STATUSES = ["pending", "approved", "processing", "completed", "rejected"];

export default function AdminWithdrawals() {
  const { t } = useI18n();
  const [list, setList] = useState(null);
  const [sel, setSel] = useState(null);
  const [status, setStatus] = useState("processing");
  const [reason, setReason] = useState("");
  const load = () => api.get("/admin/withdrawals").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!list) return <Loader />;

  const save = async () => {
    try {
      await api.put(`/admin/withdrawals/${sel.id}`, { status, reason });
      toast.success(`Withdrawal set to ${status}`); setSel(null); setReason(""); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const pending = list.filter((w) => w.status === "pending");

  return (
    <div>
      <PageHeader title={t("withdrawal_mgmt")} subtitle={`${pending.length} pending · ${list.length} total`} />
      {list.length === 0 ? <Empty label="No withdrawal requests." /> : (
        <div className="ec-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-gray-500 border-b border-white/10">
              {["Member", "Method", t("amount"), t("account_details"), t("status"), "Date", ""].map((h) => <th key={h} className="text-start font-semibold px-4 py-3">{h}</th>)}
            </tr></thead>
            <tbody>
              {list.map((w) => (
                <tr key={w.id} className="border-b border-white/5" data-testid={`admin-withdrawal-${w.id}`}>
                  <td className="px-4 py-3 text-white">{w.user_name}</td>
                  <td className="px-4 py-3 text-gray-400">{w.method}</td>
                  <td className="px-4 py-3 text-gray-200 font-semibold">{fmtPKR(w.amount)}</td>
                  <td className="px-4 py-3 text-gray-400 max-w-[220px] truncate keep-ltr" title={w.account_details}>{w.account_details}</td>
                  <td className="px-4 py-3"><Badge className={statusColor(w.status)}>{w.status}</Badge></td>
                  <td className="px-4 py-3 text-gray-500">{fmtDate(w.created_at)}</td>
                  <td className="px-4 py-3"><Button size="sm" onClick={() => { setSel(w); setStatus(w.status === "pending" ? "processing" : w.status); setReason(w.reason || ""); }} data-testid={`review-withdrawal-${w.id}`} className="bg-orange-600 hover:bg-orange-500 font-semibold">Update</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="ec-card border-white/10">
          <DialogHeader><DialogTitle className="font-head">{sel?.user_name} — {t("withdrawal")}</DialogTitle></DialogHeader>
          {sel && (
            <div className="space-y-4">
              <p className="text-sm"><span className="text-gray-500">Amount:</span> <span className="text-white font-semibold">{fmtPKR(sel.amount)}</span> · {sel.method}</p>
              <p className="text-sm text-gray-300 bg-white/5 rounded-lg p-3 keep-ltr">{sel.account_details}</p>
              <div>
                <label className="text-sm text-gray-400">Set {t("status")}</label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="mt-1 bg-white/5 border-white/10" data-testid="withdrawal-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Rejecting refunds the held amount to the member.</p>
              </div>
              <Input placeholder="Reason / note" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="withdrawal-reason-input" />
              <Button onClick={save} data-testid="withdrawal-save" className="w-full bg-orange-600 hover:bg-orange-500 btn-glow font-semibold">{t("save")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
